import { createContext, useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { sincronizarRecibosPendentes } from "../utils/recibosPagamento";

const API_URL =
  process.env.REACT_APP_API_URL ||
  (typeof window !== "undefined" && window.location.hostname === "localhost"
    ? "http://localhost:8002"
    : "https://api-eyazs-production-8f74.up.railway.app");

export const AuthContext = createContext();

function lerTokenAutenticacao() {
  try {
    return JSON.parse(localStorage.getItem("token"));
  } catch (erro) {
    return localStorage.getItem("token");
  }
}

function normalizarTexto(valor) {
  return String(valor || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase();
}

function nomePlano(profile, tipoUsuario) {
  const profileNormalizado = normalizarTexto(profile);

  if (profileNormalizado === "ESENCIAL" || profileNormalizado === "ESSENCIAL") {
    return "ESSENCIAL";
  }

  if (profile && profileNormalizado !== "DEFAULT") {
    return profile;
  }

  if (tipoUsuario === "colab") {
    return "Plano Staff";
  }

  return "Mes de Integracao";
}

function tokenInternetValido(tokenInternet) {
  if (!tokenInternet) return true;

  try {
    const dadosToken = jwtDecode(tokenInternet);
    if (!dadosToken?.exp) return true;
    return dadosToken.exp * 1000 > Date.now();
  } catch (erro) {
    return true;
  }
}

function pacoteEstaAtivo(dadosUsuario) {
  const profileOperacional = normalizarTexto(dadosUsuario.profile);
  const disabled = String(dadosUsuario.disabled || "").toLowerCase();
  const estado = String(dadosUsuario.estado || "").toLowerCase();

  if (profileOperacional === "DESATVADO" || profileOperacional === "CONGELADO") return false;
  if (disabled === "true" || estado === "desativo") return false;
  return tokenInternetValido(dadosUsuario.token);
}

export function AuthProvider({ children }) {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [autenticado, setAutenticado] = useState(false);
  const [saldo, setSaldo] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [plan, setPlan] = useState();
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoadiang] = useState(false);
  const [usuario, setUsuario] = useState({});

  const aplicarDadosUsuario = useCallback((dadosUsuario, { guardarProfile = true } = {}) => {
    if (!dadosUsuario) return;

    const profileLocal = localStorage.getItem("profileAtual");
    const profileOperacional = dadosUsuario.profile || profileLocal || dadosUsuario.profileusuario || "";
    const profileVisivel = dadosUsuario.profileusuario || profileOperacional;
    const admin = dadosUsuario.tipoUsuario === "admin" || dadosUsuario.tipoUsuario === "super";

    setUser(dadosUsuario.name);
    setUsuario(dadosUsuario);
    setIsAdmin(admin);
    setSaldo(pacoteEstaAtivo({ ...dadosUsuario, profile: profileOperacional }));
    setPlan(nomePlano(profileVisivel || profileOperacional, dadosUsuario.tipoUsuario));
    setAutenticado(true);

    if (guardarProfile) {
      localStorage.setItem("profileAtual", profileVisivel || profileOperacional || "");
    }
  }, []);

  const verificarLogin = useCallback(() => {
    try {
      const tokenLocalStorage = lerTokenAutenticacao();

      if (!tokenLocalStorage) {
        setAutenticado(false);
        setIsAdmin(false);
        setSaldo(false);
        return;
      }

      const dadosUsuario = jwtDecode(tokenLocalStorage);
      const profileAtual = localStorage.getItem("profileAtual");

      aplicarDadosUsuario(
        {
          ...dadosUsuario,
          profile: profileAtual || dadosUsuario.profile,
          profileusuario: profileAtual || dadosUsuario.profileusuario,
        },
        { guardarProfile: false }
      );
    } catch (erro) {
      console.log(erro);
      setAutenticado(false);
      setIsAdmin(false);
      setSaldo(false);
    }
  }, [aplicarDadosUsuario]);

  const atualizarUsuarioRemoto = useCallback(async () => {
    const token = lerTokenAutenticacao();
    if (!token) return null;

    try {
      const dadosToken = jwtDecode(token);
      if (!dadosToken?.idUsuario) return null;

      const resposta = await axios.get(`${API_URL}/usuario/${dadosToken.idUsuario}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      const dadosAtualizados = { ...dadosToken, ...resposta.data };

      aplicarDadosUsuario(dadosAtualizados);
      await sincronizarRecibosPendentes({ apiUrl: API_URL, token });
      return dadosAtualizados;
    } catch (erro) {
      verificarLogin();
      return null;
    }
  }, [aplicarDadosUsuario, verificarLogin]);

  async function fazerLogin(username, password) {
    setLoadiang(true);

    try {
      const data = await axios.post(`${API_URL}/login`, {
        username,
        password,
      });
      const token = data.data.token;

      if (!token) {
        setErrorMessage("usuario ou senha inavalidos");
        return;
      }

      setErrorMessage("");
      localStorage.setItem("token", JSON.stringify(token));
      const dadosUsuario = jwtDecode(token);

      let dadosAtualizados = dadosUsuario;
      try {
        const resposta = await axios.get(`${API_URL}/usuario/${dadosUsuario.idUsuario}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
        dadosAtualizados = { ...dadosUsuario, ...resposta.data };
      } catch (erro) {
        dadosAtualizados = dadosUsuario;
      }

      aplicarDadosUsuario(dadosAtualizados);

      if (dadosAtualizados.tipoUsuario === "admin" && dadosAtualizados.estado === "desativo") {
        setErrorMessage("Acesso Negado!, consulte seu administrador!");
        return;
      }

      if (dadosAtualizados.tipoUsuario === "admin") {
        navigate("/admin");
      } else if (dadosAtualizados.tipoUsuario === "super") {
        navigate("/super");
      } else {
        navigate("/home");
      }
    } catch (error) {
      setErrorMessage("usuario ou senha invalidos, verifique e tente novamente!");
      console.log(error);
    } finally {
      setLoadiang(false);
    }
  }

  function logout() {
    localStorage.removeItem("token");
    localStorage.removeItem("profileAtual");
    setUser(null);
    setUsuario({});
    setAutenticado(false);
    setIsAdmin(false);
    setSaldo(false);
    setPlan(null);
    navigate("/");
  }

  function atualizarPlanoLocal(profile) {
    const profileFinal = profile || "";
    localStorage.setItem("profileAtual", profileFinal);
    setSaldo(normalizarTexto(profileFinal) !== "DESATVADO");
    setPlan(nomePlano(profileFinal));
    setUsuario((usuarioAtual) => ({
      ...usuarioAtual,
      profile: profileFinal,
      profileusuario: profileFinal,
      disabled: "false",
    }));
  }

  useEffect(() => {
    verificarLogin();
    atualizarUsuarioRemoto();

    const intervalo = setInterval(atualizarUsuarioRemoto, 5000);
    window.addEventListener("focus", atualizarUsuarioRemoto);
    window.addEventListener("online", atualizarUsuarioRemoto);

    return () => {
      clearInterval(intervalo);
      window.removeEventListener("focus", atualizarUsuarioRemoto);
      window.removeEventListener("online", atualizarUsuarioRemoto);
    };
  }, [verificarLogin, atualizarUsuarioRemoto]);

  return (
    <AuthContext.Provider
      value={{
        loading,
        usuario,
        setPlan,
        setLoadiang,
        user,
        autenticado,
        isAdmin,
        plan,
        saldo,
        setErrorMessage,
        errorMessage,
        fazerLogin,
        logout,
        verificarLogin,
        atualizarUsuarioRemoto,
        atualizarPlanoLocal,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
